import axios from 'axios';

import {
  loadedSiteData,
  pageUpdated,
  pageAdded,
  pageDeleted,
  blogAdded,
  blogDeleted,
  blogUpdated,
  blogRenamed,
  contentSectionUpdated
} from 'RLX/State/Content/ContentCreators';

import RLXStore from 'RLX/State/Store';
import { SERVER_URL, AUTOSAVE_ENABLED } from 'RLX/Config';
import { getEmptyPage, getEmptyBlog } from 'Core/Utils/ContentUtils';
import {
  handleError,
  updateSavedTime,
  initialStateLoaded,
  declareSavedChanges,
  declareUnsavedChanges
} from 'RLX/State/Ui/UiActions';

export function loadSite () {
  if (!RLXStore.getState().auth.loggedIn) return Promise.reject('Cant load site not logged in');

  return axios.get(SERVER_URL + '/data')
    .then(({ data }) => RLXStore.dispatch(loadedSiteData(data)))
    .then(() => initialStateLoaded())
    .catch(error => handleError(error));
}

export function publishSite () {
  if (confirm('All changes will appear immediately on live site. Click OK to continue.')) {
    return axios.post(SERVER_URL + '/publish', {})
      .then(res => {
        // alert('Successfully published changes to live site.');
      })
      .catch(err => {
        alert('An error occurred publishing your changes. Check the console.');
        console.error(err)
      });
  }
}

export function updatePage (page) {
  if (typeof page !== 'object' || !'id' in page) handleError('Invalid page provided.');
  RLXStore.dispatch(pageUpdated(page));
  declareUnsavedChanges();
}

export function deletePage (id) {
  if (!confirm('Are you sure you want to delete this page? This action cannot be undone.')) return;
  if (typeof id !== 'string') handleError('Invalid page ID to be deleted provided.');
  RLXStore.dispatch(pageDeleted(id));
  declareUnsavedChanges();
}

export function addNewPage () {
  const page = getEmptyPage();
  RLXStore.dispatch(pageAdded(page));
  declareUnsavedChanges();
}

export function renameBlog (oldBlogId, newBlogId) {
  if (typeof oldBlogId !== 'string')
    throw new TypeError('ContentActions.renameBlog: "oldBlogId" is not a string.');
  if (typeof newBlogId !== 'string')
    throw new TypeError('ContentActions.renameBlog: "newBlogId" is not a string.');
  RLXStore.dispatch(blogRenamed(oldBlogId, newBlogId));
  declareUnsavedChanges();
}

export function updateBlog (blog) {
  if (typeof blog !== 'object' || !'id' in blog) handleError('Invalid blog provided.');
  RLXStore.dispatch(blogUpdated(blog));
  declareUnsavedChanges();
}

export function deleteBlog (id) {
  if (!confirm('Are you sure you want to delete this blog? This action cannot be undone.')) return;
  if (typeof id !== 'string') handleError('Invalid blog ID to be deleted provided.');
  RLXStore.dispatch(blogDeleted(id));
  declareUnsavedChanges();
}

export function addNewBlog () {
  const blog = getEmptyBlog();
  RLXStore.dispatch(blogAdded(blog));
  declareUnsavedChanges();
}

export function updateContentSection (pageId, content) {
  if (typeof pageId !== 'string') return handleError('Invalid page ID provided.');
  if (typeof content !== 'object' || !'type' in content || !'id' in content) return handleError('Invalid content provided.');
  RLXStore.dispatch(contentSectionUpdated(pageId, content));
  declareUnsavedChanges();
}

export function getBlogs () {
  const { blogs } = RLXStore.getState().siteData;
  return blogs;
}

export function getBlog (id) {
  const blogs = getBlogs();
  const blog = blogs.find(blog => blog.id === id);
  return blog
    ? blog
    : console.error(`Blog not found: ${id}`);
}

export function getBlogPosts (id) {
  const blog = getBlog(id);
  return !blog || !'posts' in blog
    ? console.error(`Couldn't get blogposts: ${id}`)
    : blog.posts;
};

export function getPost (blogId, postSlug) {
  const posts = getBlogPosts(blogId);
  const predicate = ({ slug }) => slug === postSlug;
  if (!posts || !posts.find(predicate)) {
    handleError(`Couldn't find post: ${postSlug} [in blog: ${blogId}]`);
  };
  return posts.find(predicate);
}

function siteDataIsValid ({ meta, pages, blogs }) {
  if (typeof meta !== 'object' || !Object.keys(meta).length)
    return false;
  else if (!Array.isArray(pages) || !pages.length)
    return false;
  else if (!Array.isArray(blogs))
    return false;
  else return true;
}

export function saveChanges () {
  const { siteData, auth } =  RLXStore.getState();

  if (!auth.loggedIn)
    return handleError(`Can't save; Not logged in!`);
  if (!siteDataIsValid(siteData))
    return handleError({ message: `Can't save: Invalid/empty site data.`, siteData });

  return axios.put(SERVER_URL + '/data', siteData)
    .then(() => {
      if (!AUTOSAVE_ENABLED) alert('Changes have been saved.')
      updateSavedTime();
      declareSavedChanges();
    }).catch(error => handleError(error));
};



// WEBPACK FOOTER //
// ./client/RLX/State/Content/ContentActions.js